import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Save, X, BellRing } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Field, OLIVE, errMsg, fmtDateTime } from "./dpaUtils";

const KIND_STYLE = { chase: "bg-amber-100 text-amber-900", overdue: "bg-red-100 text-red-800", verification: "bg-sky-100 text-sky-800" };

const PolicyCard = ({ settings, onSaved }) => {
  const [f, setF] = useState({ ...settings, chase_text: settings.chase_days.join(", "), recipients_text: settings.recipients.join(", ") });
  const save = async () => {
    const body = { ...f, chase_days: f.chase_text.split(/[,\s]+/).filter(Boolean).map(Number).filter((n) => n > 0),
      recipients: f.recipients_text.split(/[,\s]+/).filter(Boolean), overdue_day: Number(f.overdue_day),
      overdue_repeat_days: Number(f.overdue_repeat_days), verification_chase_days: Number(f.verification_chase_days) };
    try { onSaved((await api.put("/dpa/reminder-settings", body)).data); toast.success("Reminder policy saved"); }
    catch (e) { toast.error("Could not save policy", { description: errMsg(e) }); }
  };
  return (
    <Card className="rounded-none p-6 space-y-4" data-testid="reminder-policy-card">
      <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Signature-chasing policy</div>
      <p className="text-sm text-muted-foreground max-w-3xl">
        Only issued DPAs still waiting for signatures are chased. Each step fires once, a newer step replaces the older one, and reminders
        clear automatically once the DPA is signed and verified. Checked daily at 07:00 UK time.
      </p>
      <div className="grid md:grid-cols-3 gap-3">
        <Field label="Gentle chases (days after issue)" value={f.chase_text} onChange={(v) => setF({ ...f, chase_text: v })} testId="policy-chase-days" hint="e.g. 7, 14" />
        <Field label="Overdue escalation from day" type="number" value={f.overdue_day} onChange={(v) => setF({ ...f, overdue_day: v })} testId="policy-overdue-day" />
        <Field label="Then repeat every (days)" type="number" value={f.overdue_repeat_days} onChange={(v) => setF({ ...f, overdue_repeat_days: v })} testId="policy-repeat-days" />
        <Field label="Chase verification (days after both signed)" type="number" value={f.verification_chase_days} onChange={(v) => setF({ ...f, verification_chase_days: v })} testId="policy-verify-days" />
        <div className="md:col-span-2"><Field label="Email recipients" value={f.recipients_text} onChange={(v) => setF({ ...f, recipients_text: v })} testId="policy-recipients" hint="Comma-separated. Leave blank to email every Super Admin." /></div>
      </div>
      <div className="flex items-center gap-6 flex-wrap">
        <label className="flex items-center gap-2 text-sm"><Switch checked={f.enabled} onCheckedChange={(v) => setF({ ...f, enabled: v })} data-testid="policy-enabled" />Reminders on</label>
        <label className="flex items-center gap-2 text-sm"><Switch checked={f.email_enabled} onCheckedChange={(v) => setF({ ...f, email_enabled: v })} data-testid="policy-email-enabled" />Email (Postmark)</label>
        <Button className="rounded-none text-white ml-auto" style={OLIVE} onClick={save} data-testid="policy-save-btn"><Save className="h-4 w-4 mr-1" />Save policy</Button>
      </div>
    </Card>
  );
};

const ReminderRow = ({ r, onDismiss }) => (
  <div className="flex items-start gap-3 border-t py-3 text-sm" style={{ borderColor: "var(--hi-border)" }} data-testid={`reminder-${r.id}`}>
    <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${KIND_STYLE[r.kind]}`}>{r.kind}</span>
    <div className="flex-1">
      <div><Link to={`/compliance/agreements/${r.agreement_id}`} className="font-mono text-blue-700">{r.reference}</Link> · {r.organisation} · <b>{r.title}</b></div>
      <div className="text-xs text-muted-foreground">{r.message} · raised {fmtDateTime(r.created_at)}{r.emailed?.length ? ` · emailed ${r.emailed.join(", ")}` : ""}</div>
      {r.status !== "open" && <div className="text-[11px] text-muted-foreground">{r.status} {fmtDateTime(r.resolved_at)}{r.resolved_reason && ` — ${r.resolved_reason}`}</div>}
    </div>
    {onDismiss && <Button size="sm" variant="ghost" className="h-7" onClick={() => onDismiss(r.id)} data-testid={`reminder-dismiss-${r.id}`}><X className="h-3.5 w-3.5 mr-1" />Dismiss</Button>}
  </div>
);

export const RemindersTab = () => {
  const [d, setD] = useState(null);
  const load = () => api.get("/dpa/reminders").then(({ data }) => { setD(data); window.dispatchEvent(new Event("dpa-reminders-changed")); });
  useEffect(() => { load(); }, []);
  if (!d) return null;
  const run = async () => {
    try { const { data } = await api.post("/dpa/reminders/run"); toast.success(`Check complete — ${data.created ?? 0} new, ${data.resolved ?? 0} cleared`); load(); }
    catch (e) { toast.error("Run failed", { description: errMsg(e) }); }
  };
  const dismiss = async (id) => { try { await api.post(`/dpa/reminders/${id}/dismiss`); load(); } catch (e) { toast.error("Failed", { description: errMsg(e) }); } };
  return (
    <div className="space-y-5" data-testid="reminders-tab">
      <Card className="rounded-none p-6">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-2"><BellRing className="h-4 w-4" />Open reminders ({d.open.length})</div>
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            Last check: {d.last_run ? fmtDateTime(d.last_run.at) : "never"}
            <Button variant="outline" className="rounded-none" onClick={run} data-testid="reminders-run-btn"><Play className="h-4 w-4 mr-1" />Run check now</Button>
          </div>
        </div>
        {d.open.map((r) => <ReminderRow key={r.id} r={r} onDismiss={dismiss} />)}
        {!d.open.length && <div className="text-sm text-muted-foreground py-4" data-testid="reminders-empty">Nothing to chase — every issued DPA is signed and verified, or still inside its grace period.</div>}
      </Card>
      <PolicyCard settings={d.settings} onSaved={(s) => setD({ ...d, settings: s })} />
      {d.recent.length > 0 && (
        <Card className="rounded-none p-6" data-testid="reminders-history">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Recently cleared</div>
          {d.recent.map((r) => <ReminderRow key={r.id} r={r} />)}
        </Card>
      )}
    </div>
  );
};
